import { useContext, useState, useEffect } from 'react';
import { Modal, Select, Tabs } from 'antd';
import Icon from '../../../components/UI/Icon/Icon';
import ClientProfilePaymentMethodSelection from '../../../components/ClientProfile/ClientProfilePaymentMethodSelection';
import { useMutation, useQuery } from '@tanstack/react-query';
import { buyProductAndUpdateAttendance, fetchPasses, refreshFetchFunctions } from '../../../apiFunctions/apiFunctions';
import { returnErrorFromResponse } from '../../../shared/utility';
import Button from '../../../components/UI/Button/Button';
import { NotificationContext } from '../../../context/Notification/NotificationContext';
import ModalButtonsContainer from '../../../components/UI/ModalButtonsContainer/ModalButtonsContainer';
import NoResultsFound from '../../../components/UI/NoResultsFound/NoResultsFound';
import Notification from '../../../components/UI/Notification/Notification';
import useTabIndex from '../../../hooks/useTabIndex';

const SelectAndPayForProduct = ({ selectedUser, singleClass, attendanceId }) => {
  const { openNotification } = useContext(NotificationContext);
  const isOnlySinglePaymentAvailable =
    (selectedUser.customerType === 'full-member' && singleClass.__t === 'class') || singleClass.__t === 'induction';
  const { activeTabIndex, onTabChange } = useTabIndex();
  useEffect(() => {
    if (isOnlySinglePaymentAvailable && activeTabIndex === 0) {
      onTabChange(1);
    }
  }, [isOnlySinglePaymentAvailable, activeTabIndex, onTabChange]);
  const [showModal, setShowModal] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);

  const {
    data: passesList,
    isLoading: passesListIsLoading,
    isError: passesListIsError
  } = useQuery(['passesList', singleClass.__t], () => fetchPasses(singleClass.__t, singleClass.gymId._id), {
    enabled: showModal && !isOnlySinglePaymentAvailable
  });

  const [selectedPassProduct, setSelectedPassProduct] = useState(null);

  const renderProducts = () => {
    if (passesList && passesList.length > 0) {
      return (
        <>
          <div className="font-bold">Select pass product</div>
          <Select
            className="w-full"
            options={passesList.map((elem) => ({
              label: `${elem.name} (x${elem.classCount})`,
              value: elem._id
            }))}
            onChange={setSelectedPassProduct}
          />
          <div className="font-bold">Choose payment method</div>
          <ClientProfilePaymentMethodSelection onMethodSelected={setSelectedPaymentMethod} />
        </>
      );
    }
    if (passesListIsError) {
      return <Notification defaultMsg />;
    }
    return !passesListIsLoading && <NoResultsFound text="No passes" />;
  };
  const tabsItems = [
    !isOnlySinglePaymentAvailable && {
      key: '0',
      label: <div className="font-bold text-lg">Choose pass product</div>,
      children: renderProducts(),
      meta: { isSinglePayment: false }
    },
    {
      key: '1',
      label: <div className="font-bold text-lg">Make single payment</div>,
      children: (
        <>
          <div className="font-bold">Choose payment method</div>
          <ClientProfilePaymentMethodSelection onMethodSelected={setSelectedPaymentMethod} />
        </>
      ),
      meta: { isSinglePayment: true }
    }
  ].filter(Boolean);
  const currentTab = tabsItems.find((tab) => tab.key === activeTabIndex.toString());
  const isSinglePayment = currentTab?.meta?.isSinglePayment ?? false;

  const { mutate: onBuyProductAndUpdateAttendance, isLoading: onBuyProductAndUpdateAttendanceIsLoading } = useMutation(
    () =>
      buyProductAndUpdateAttendance(
        attendanceId,
        selectedPaymentMethod,
        selectedUser._id,
        !isSinglePayment ? selectedPassProduct : null,
        isSinglePayment
      ),
    {
      onSuccess: () => {
        openNotification(null, 'success', `Attendance successfully updated.`);
        refreshFetchFunctions([['singleClass', singleClass._id]]);
      },
      onError: (err) => {
        openNotification(null, 'error', returnErrorFromResponse(err));
      }
    }
  );

  return (
    <>
      <Modal open={showModal} onCancel={() => setShowModal(false)}>
        <Tabs
          activeKey={activeTabIndex.toString()}
          onChange={(key) => onTabChange(Number(key))}
          items={tabsItems}
          defaultActiveKey={activeTabIndex.toString()}
        />

        <ModalButtonsContainer isLoading={onBuyProductAndUpdateAttendanceIsLoading}>
          <Button label="OK" secondary onClick={onBuyProductAndUpdateAttendance} />
          <Button label="Cancel" primary onClick={() => setShowModal(false)} />
        </ModalButtonsContainer>
      </Modal>
      <Icon name="money" onClick={() => setShowModal(true)} />
    </>
  );
};

export default SelectAndPayForProduct;
